// NuestraHistoria.jsx
import React from 'react';
import {
  PageWrapper,
  HeroSection,
  ContentSection,
  TextBlock,
  StatementHeading,
  FullWidthImage,
  TwoColImages,
} from './PageTemplate';
import ProductBlock from '../../components/ProductBlock';
import SEO from '../../components/SEO';

const IMAGES = {
  fullWidth1: '/images/secciones/nuestra-historia/Kit_minimalista_piel_protegida_physiology_skincare_banner-scaled.webp',
  fullWidth2: '/images/secciones/curated-selection/cream4.webp',
  grid: ['/images/secciones/nuestra-historia/Soma_lacta_tonic_exfoliante_tonico_physiology_skincare.webp', '/images/secciones/nuestra-historia/Gotu_kola_esencia_ultrahidratante_physiology_skincare.webp'],
};

const PRODUCTS = [
  {
    id: 'historia-prod1',
    title: 'Vitamina C Pura - Drop 30 mL',
    description: 'Aporta luminosidad con antioxidantes',
    price: 750,
    image: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/vitamina_12_Serum_luminosidad_physiology_skincare_12%25-01KWV2NHKDD8YNKY1CVE8DJ00X.webp',
    thumbnail: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/vitamina_12_Serum_luminosidad_physiology_skincare_12%25-01KWV2NHKDD8YNKY1CVE8DJ00X.webp',
    handle: 'vitamina-c-pura',
    variants: [{ id: 'v1', title: '2 Porcentajes', label: '2 Porcentajes', price: 750 }],
    inStock: true,
    badge: 'essential',
  },
  {
    id: 'historia-prod2',
    title: 'Ultra Barrier Repair',
    description: 'Revitaliza y refuerza nuestra piel a agentes externos',
    price: 800,
    originalPrice: 1200,
    image: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/Hydra_Cream_crema_Ultrahidratante_Front_100_Ml-01KW8TDEHHMC3QAAN5ZV5YXA3Y.webp',
    thumbnail: 'https://pub-af8345b2339e47c4a9067290386af123.r2.dev/Hydra_Cream_crema_Ultrahidratante_Front_100_Ml-01KW8TDEHHMC3QAAN5ZV5YXA3Y.webp',
    handle: 'ultra-barrier-repair',
    variants: [{ id: 'v2', title: '60 mL', label: '60 mL', price: 800 }],
    inStock: true,
  },
];

const NuestraHistoria = () => {
  return (
    <PageWrapper>
      <SEO
        title="Nuestra Historia — Formulando arte desde 2017"
        description="Physiology nació para transformar el cuidado de la piel en un acto de arte: fórmulas minimalistas, eficaces y con precios realistas, entre ciencia y emoción."
        path="/nuestra-historia"
      />

      {/* ── Hero ── */}
      <HeroSection
        category="Sobre nosotros"
        title="Ciencia y belleza, un mismo lenguaje"
        meta="Fundada en 2017"
      />

      {/* ── Imagen full-width ── */}
      <FullWidthImage src={IMAGES.fullWidth1} alt="Nuestra historia" />

      {/* ── Introducción ── */}
      <ContentSection narrow>
        <TextBlock
          body="En Physiology creemos que la ciencia no está reñida con la belleza, sino que juntas pueden crear un lenguaje nuevo: un cuidado de la piel que es a la vez preciso y poético, clínico y humano."
        />
        <TextBlock
          body="Nacimos para simplificar lo complejo, para demostrar que el minimalismo es también un acto de amor propio. No se trata de tener más pasos, sino de elegir lo esencial: fórmulas innovadoras que funcionan, experiencias sensoriales que inspiran, rituales que devuelven equilibrio a la piel y al espíritu."
        />
      </ContentSection>

      {/* ── Statement + historia ── */}
      <ContentSection narrow>
        <StatementHeading text="Physiology es más que cosmética: es un recordatorio de que cuidar la piel es también cuidar la vida." />
        <TextBlock
          heading="Nuestra historia"
          body="Physiology nació para transformar el cuidado de la piel en un acto de arte. Desde el inicio, nuestro propósito fue claro: simplificar la belleza, devolverla a su esencia, y convertir cada fórmula en un puente entre ciencia y emoción."
        />
        <TextBlock
          body="Fundada en 2017, surgió como un sueño sencillo y honesto: crear fórmulas eficaces con precios realistas. Con el tiempo, ese sueño se convirtió en un lenguaje propio: experiencias sensoriales con resultados visibles, nacidas no solo para embellecer, sino para recordar que cuidar la piel es también un acto de amor propio."
        />
        <TextBlock
          body="No buscamos únicamente resaltar un rostro. Buscamos inspirar bienestar físico y mental, porque la verdadera belleza es consecuencia de una piel saludable y de un espíritu en equilibrio. Cada fórmula, cada textura, cada detalle que creamos responde a esa visión: productos que cuidan, acompañan y respetan la esencia de cada persona."
        />
      </ContentSection>

      {/* ── Grid de 2 imágenes ── */}
      <ContentSection>
        <TwoColImages images={IMAGES.grid} />
      </ContentSection>

      {/* ── Formulando arte ── */}
      <ContentSection narrow>
        <StatementHeading text="“Formulando arte”" />
        <TextBlock
          heading="Ciencia que respira arte"
          body="Cada producto de Physiology es fruto de la formulación minimalista, donde la ciencia se convierte en poesía tangible. Desde limpiadores hasta sueros, cada detalle está pensado con precisión clínica y sensibilidad artística. Cada gota, cada molécula, responde a un propósito claro: proteger, restaurar y embellecer sin exceso."
        />
        <TextBlock
          body="“Formulando arte” no es solo nuestro lema: es nuestra manera de concebir la cosmética como un diálogo entre innovación y estética, entre moléculas y emociones, entre la exactitud científica y la inspiración artística."
        />
      </ContentSection>

      {/* ── Imagen full-width ── */}
      <FullWidthImage src={IMAGES.fullWidth2} alt="Physiology Skincare" />

      {/* ── Comunidad + invitación ── */}
      <ContentSection narrow>
        <TextBlock
          heading="Una comunidad que nos inspira"
          body="Ser minimalista no significa ser básico. Significa tener el valor de poner atención en lo esencial, dejando atrás lo superfluo. Es comprender que menos es más: una verdad científica y, al mismo tiempo, un arte de vida."
        />
        <TextBlock
          body="La sencillez conecta, libera y nos devuelve al centro de nosotros mismos. Y desde ahí, todo se vuelve posible. Una piel cuidada no es el fin, sino el comienzo de una vida más consciente y plena."
        />
        <TextBlock
          heading="Una invitación"
          body="Physiology es la sinergia entre ciencia y arte, entre eficacia y belleza. Es un recordatorio de que cuidar la piel no es un lujo: es un acto de alegría, de respeto y de amor propio."
        />
      </ContentSection>

      {/* ── Productos relacionados ── */}
      <ProductBlock
        title="Productos relacionados"
        description="Descubre los esenciales de Physiology"
        buttonText="Ver todos"
        products={PRODUCTS}
      />

      {/* ── Cierre ── */}
      <ContentSection narrow>
        <StatementHeading text="Da alegría a tu vida. Da alegría a tu piel. Da alegría a tu ser." />
      </ContentSection>

    </PageWrapper>
  );
};

export default NuestraHistoria;
